import * as R from 'ramda'
import {
  Building,
  CommercialTerms,
  EquipmentCategory,
  EquipmentGroup,
  EquipmentGroups,
  RatingsAndSelections,
  EquipmentGroupStId,
  Price,
  ClientProfile,
  DiscountLimits,
  ClientFrontlineConfig,
  Selections,
  OperationalEnvironment,
  Equipment,
  EquipmentDynamicField,
  Opportunity
} from 'app/types/types'
import { getEquipmentByGroup, getEquipmentForGroup } from '../nemo/offer'
import { countByEquipmentCategory, sortEquipmentCategoriesComparator } from './grouping-utils'
import { Icons, Constants, PRICING_PROFILE, PRICING_PERIOD } from './client-constants'
import { capitalizeIfEnglish, getEquipmentText } from './utils'
import { t } from 'i18next'
import { calculatePrices } from './pricing-package/price-calculation'
import { MBMData, PriceResult, PricingConfig, ProfileData } from 'app/types/pricing'
import { PricingCategory } from 'app/types/tables'
import { formatPrice, PriceScoper } from 'app/app/(client)/utils/utils'
import { EquipmentsPriceResult, EquipmentWithPriceResult, PricingSummaryGroupProps } from 'app/types/ui/pricing'
import { vatPriceBreakdownCalulator } from 'app/app/common/vat-utils'
import {
  DiscountApprovalLevels,
  DiscountIsLimitedBy,
  GroupPriceBreakdown,
  VatBreakdown,
  VatBreakdownConfig
} from 'app/app/common/price-breakdown-types'
import moment from 'moment'
import { contractTiers } from '../components/organisms/ServiceConfigurationContent/constants'
import { elevatorPricingConfig, escalatorPricingConfig } from './pricing-package/price-calculation-util'

export const pricingPeriodOptions = [
  { key: 'monthly', value: 'offering.refresh.monthly' },
  { key: 'yearly', value: 'offering.refresh.yearly' }
]

const sortEquipmentByCategory = (equipmentArray: Equipment[] | undefined): Equipment[] =>
  equipmentArray?.sort((a, b) => sortEquipmentCategoriesComparator(a.equipmentCategory, b.equipmentCategory)) || []

const calculatePriceIncludingVat = (vatBreakdownConfig: VatBreakdownConfig[], basePrice: number): number => {
  const vatBreakdown = vatPriceBreakdownCalulator(vatBreakdownConfig, basePrice)
  return vatBreakdown.reduce((acc, vat) => acc + vat.annualVatPrice, basePrice)
}

const sumGroupPrices = (groupsData: PricingSummaryGroupProps[]): number =>
  groupsData.reduce((acc, group) => acc + group.price, 0)

export const calculateDiscountedPrice = (price: number, discountPercent: number): number =>
  price * (1 - discountPercent / 100)

const pricingEquipmentCategoryMap: Record<string, PricingCategory> = {
  elevator: 'new_elevator',
  escalator: 'new_escalator',
  door: 'door',
  resiflow: 'resiflow',
  interaction: 'interaction',
  additional_services: 'additional_services',
  value_added_services_addendum: 'value_added_services_addendum',
  kone_care_taker: 'kone_care_taker',
  gsm_campaign: 'gsm_campaign'
}

const pricingEquipmentConfigMap: Record<string, PricingConfig[]> = {
  elevator: elevatorPricingConfig,
  escalator: escalatorPricingConfig
}

export const getGroupEquipmentDataForPricingSummary = (
  buildings: Building[],
  equipmentGroups: EquipmentGroups,
  prices: Price[]
) => {
  if (!equipmentGroups) {
    return [] as PricingSummaryGroupProps[]
  }

  const groupSalesToolIdArray: string[] = Object.keys(equipmentGroups)
  const equipmentArrayBasedOnGroupId = getEquipmentByGroup(buildings)

  const processedDataByGroup: PricingSummaryGroupProps[] = groupSalesToolIdArray.map((groupId: string) => {
    const sortedEquipmentArray = sortEquipmentByCategory(equipmentArrayBasedOnGroupId[groupId])

    const equipmentCountBasedOnEquipmentCategory = countByEquipmentCategory(sortedEquipmentArray)

    const equipmentData = Object.keys(equipmentCountBasedOnEquipmentCategory).map(key => ({
      element: Icons[key as keyof typeof Icons],
      count: equipmentCountBasedOnEquipmentCategory[key],
      elementDescription: getEquipmentText(key as EquipmentCategory, equipmentCountBasedOnEquipmentCategory[key]),
      category: key as EquipmentCategory
    }))

    const { price, discountedPrice } = prices.reduce(
      (acc, curr) => {
        if (curr.groupId === groupId) {
          acc.price += curr.equipmentPrice
          acc.discountedPrice += curr.discountedPrice
        }
        return acc
      },
      { price: 0, discountedPrice: 0 }
    )

    const discount = calculateDiscountFromPrice(price, discountedPrice)

    const { salesToolId: id, name } = equipmentGroups[groupId]

    const currentDataObj: PricingSummaryGroupProps = {
      id,
      name,
      equipmentData,
      price,
      discount,
      discountedPrice
    }

    return currentDataObj
  })
  const equipmentDataSortByGroup = R.sortBy(groupDetail => groupDetail.name.toLowerCase(), processedDataByGroup)

  return equipmentDataSortByGroup
}

export const getGroupWisePriceByPricingPeriod = (
  groupWisePrice: PricingSummaryGroupProps[],
  selectedKey: string,
  sourcePricingPeriod: string = PRICING_PERIOD.YEARLY
) => {
  if (selectedKey === sourcePricingPeriod) {
    return groupWisePrice
  }

  const newPriceScoper = PriceScoper(selectedKey === PRICING_PERIOD.MONTHLY)

  return groupWisePrice.map(group => ({
    ...group,
    discountedPrice: newPriceScoper.price(group.discountedPrice),
    price: newPriceScoper.price(group.price)
  }))
}

export const getEquipmentDetailsForSelectedGroup = (
  buildings: Building[],
  equipmentGroupId: EquipmentGroupStId,
  prices: Price[],
  language: string,
  currency: string
) => {
  const allEquipmentBasedOnGroupId = getEquipmentForGroup(equipmentGroupId, buildings)

  const sortedEquipmentArray = sortEquipmentByCategory(allEquipmentBasedOnGroupId)

  const equipmentCountBasedOnEquipmentCategory = countByEquipmentCategory(sortedEquipmentArray)

  const equipmentCategorySwitcherOptions = Object.keys(equipmentCountBasedOnEquipmentCategory).map(currEquipment => ({
    key: currEquipment,
    value: `${t(
      getEquipmentText(currEquipment as EquipmentCategory, equipmentCountBasedOnEquipmentCategory[currEquipment])
    )} (${equipmentCountBasedOnEquipmentCategory[currEquipment]})`
  }))

  const equipmentWithPriceData: EquipmentWithPriceResult[] = []

  buildings.forEach(building => {
    const {
      details: { equipment: equipments }
    } = building

    const buildingEquipments = equipments.filter(equipment => equipment.groupId === equipmentGroupId)

    buildingEquipments.forEach(equipmentItem => {
      const equipmentWithPrice = prices.find(equip => equip.equipmentId === equipmentItem.salesToolId)
      if (equipmentWithPrice) {
        const { equipmentPrice, calculatedPrice, discountedPrice } = equipmentWithPrice

        equipmentWithPriceData.push({
          ...equipmentItem,
          priceResults: calculatedPrice,
          price: equipmentPrice,
          discountedPrice,
          priceString: formatPrice(discountedPrice, language, currency)
        })
      }
    })
  })

  return { equipmentWithPriceData, equipmentCategorySwitcherOptions }
}

const getTechnicalPlatformCategory = (name: string, categories: Record<string, string>): string =>
  R.propOr('', name, categories)

const getCarDoorTypeCategory = (
  carDoorType: string | null,
  equipmentDynamicFields: EquipmentDynamicField[]
): string => {
  const carDoorTypeConfig = equipmentDynamicFields.find(field => field.key === 'carDoorType')
  const priceDrivers = carDoorTypeConfig?.price_drivers
  if (!priceDrivers) {
    return ''
  }
  return carDoorType && priceDrivers[carDoorType] ? priceDrivers[carDoorType] : 'Category 0'
}

export const getEquipmentProfile = (
  equipmentDetails: Equipment,
  commercialTerms: CommercialTerms,
  selections: Selections,
  equipmentProfileName: string,
  opportunity: Opportunity,
  frontLineConfig: ClientFrontlineConfig,
  isKoneCareTier: boolean
): ProfileData => {
  const { technicalPlatforms, escalatorTypes, operationalEnvironments, equipmentDynamicFields } = frontLineConfig
  const { equipmentCategory, operationalEnvironment, year, criticality } = equipmentDetails
  const { marketSegment, fundingSector, opportunityCategory } = opportunity

  const currentYear = moment().year()
  const rawAge = typeof year === 'number' ? currentYear - year : 0
  const roundedAge = rawAge >= 0 ? rawAge : 0

  const baseProfile: ProfileData = R.mergeAll([
    { isKoneCareTier },
    selections,
    equipmentDetails,
    commercialTerms,
    {
      [PRICING_PROFILE.age]: roundedAge,
      [PRICING_PROFILE.equipmentName]: equipmentProfileName,
      [PRICING_PROFILE.segment]: marketSegment,
      [PRICING_PROFILE.fundingSector]: fundingSector,
      [PRICING_PROFILE.equipmentCriticality]: criticality,
      [PRICING_PROFILE.opportunityCategory]: opportunityCategory,
      [PRICING_PROFILE.KCSM_CARE_MBM]: '0',
      [PRICING_PROFILE.operationalEnvironment]: R.prop(
        operationalEnvironment as OperationalEnvironment,
        operationalEnvironments
      )
    }
  ])

  const equipmentProfile: ProfileData = ((): ProfileData => {
    switch (equipmentCategory) {
      case 'elevator':
        return R.mergeAll([
          baseProfile,
          {
            [PRICING_PROFILE.usage]: equipmentDetails.startsPerYear,
            [PRICING_PROFILE.speed]: equipmentDetails.ratedSpeed,
            [PRICING_PROFILE.load]: equipmentDetails.ratedLoad,
            [PRICING_PROFILE.numberOfElevators]: 1,
            [PRICING_PROFILE.numberOfDoors]: equipmentDetails.doorsCount,
            [PRICING_PROFILE.technicalPlatform]: getTechnicalPlatformCategory(
              equipmentDetails.technicalPlatform as string,
              technicalPlatforms
            ),
            [PRICING_PROFILE.carDoorType]: getCarDoorTypeCategory(equipmentDetails.carDoorType, equipmentDynamicFields)
          }
        ])
      case 'escalator':
        return R.mergeAll([
          baseProfile,
          {
            [PRICING_PROFILE.numberOfEscalators]: 1,
            [PRICING_PROFILE.technicalPlatform]: getTechnicalPlatformCategory(
              equipmentDetails.escalatorType as string,
              escalatorTypes
            ),
            [PRICING_PROFILE.escalatorOperationalEnvironment]: equipmentDetails.operationalEnvironmentCondition
          }
        ])
      default:
        return baseProfile
    }
  })()

  return equipmentProfile
}

export const getPricesForCurrentOffer = (
  buildings: Building[],
  equipmentGroups: EquipmentGroups,
  commercialTerms: CommercialTerms,
  opportunity: Opportunity,
  frontLineConfig: ClientFrontlineConfig,
  discounts?: Record<EquipmentGroupStId, number>
): EquipmentsPriceResult => {
  const {
    pricing: { variables: pricingVariables, options }
  } = frontLineConfig
  if (!equipmentGroups || !pricingVariables) {
    return { equipmentPrice: [], isValidPrice: false } as EquipmentsPriceResult
  }

  const groupIds: EquipmentGroupStId[] = Object.keys(equipmentGroups)
  const equipmentArrayBasedOnGroupId: Record<EquipmentGroupStId, Equipment[]> = getEquipmentByGroup(buildings)

  const { prices: pricesForCurrentOffer, isValid: isValidPrice } = groupIds.reduce(
    (acc, groupId) => {
      const equipmentArray = equipmentArrayBasedOnGroupId[groupId] || []
      const currentEquipmentGroup: EquipmentGroup = equipmentGroups[groupId]
      const groupDiscount = discounts?.[groupId] ?? 0

      const { prices: groupPrices, isValid: isGroupValid } = equipmentArray.reduce(
        (groupAcc, equipmentDetails) => {
          const { equipmentCategory, salesToolId, inventoryNumber } = equipmentDetails
          const isKoneCareTier =
            !!currentEquipmentGroup.scopes && currentEquipmentGroup.scopes[equipmentCategory] === contractTiers.koneCare
          const equipmentBelongToGroup = currentEquipmentGroup[
            equipmentCategory as keyof typeof currentEquipmentGroup
          ] as RatingsAndSelections

          const { selections } = equipmentBelongToGroup
          const pricingEquipmentCategory: PricingCategory = pricingEquipmentCategoryMap[equipmentCategory]

          const pricingVariablesForCategory: MBMData = pricingVariables[pricingEquipmentCategory]
          const pricingConfig = pricingEquipmentConfigMap[equipmentCategory] || []
          const equipmentProfileName = inventoryNumber || capitalizeIfEnglish(equipmentCategory)
          const contractType = currentEquipmentGroup.scopes?.[equipmentCategory]
          const onDemandAllowedChoices = frontLineConfig.offerSelections.group[equipmentCategory]
            ?.find(question => question.key === 'KCSM_SR_ONDEMAND')
            ?.selections[contractType as keyof typeof frontLineConfig.offerSelections.group[typeof equipmentCategory][number]['selections']] ?? []

          const equipmentProfile: ProfileData = getEquipmentProfile(
            equipmentDetails,
            commercialTerms,
            selections,
            equipmentProfileName,
            opportunity,
            frontLineConfig,
            isKoneCareTier
          )

          const priceResults = calculatePrices(
            equipmentProfile,
            pricingVariablesForCategory,
            pricingConfig,
            options,
            onDemandAllowedChoices
          ) as PriceResult[]

          const filterPriceResultArr =
            priceResults.length > 0 ? priceResults.filter(pr => pr.priceKey === 'grandTotal') : []
          const equipmentMonthlyPrice =
            filterPriceResultArr.length > 0 && filterPriceResultArr[0].price ? filterPriceResultArr[0].price : 0

          const equipmentAnnualPrice = equipmentMonthlyPrice ? parseFloat((equipmentMonthlyPrice * 12).toFixed(2)) : 0

          const discountedEquipmentPrice = parseFloat(
            calculateDiscountedPrice(equipmentAnnualPrice, groupDiscount).toFixed(2)
          )

          return {
            prices: groupAcc.prices.concat({
              equipmentId: salesToolId,
              groupId,
              discount: groupDiscount,
              discountedPrice: discountedEquipmentPrice,
              equipmentPrice: equipmentAnnualPrice,
              calculatedPrice: priceResults,
              equipmentProfile
            }),
            isValid: groupAcc.isValid && equipmentMonthlyPrice !== 0
          }
        },
        { prices: [] as Price[], isValid: true }
      )

      return {
        prices: acc.prices.concat(groupPrices),
        isValid: acc.isValid && isGroupValid
      }
    },
    { prices: [] as Price[], isValid: true }
  )

  return { equipmentPrice: pricesForCurrentOffer, isValidPrice } as EquipmentsPriceResult
}

export const calculatePricingSummaryTotals = (
  groupsData: PricingSummaryGroupProps[],
  vatBreakdownConfig: VatBreakdownConfig[]
) => {
  const totalListPrice = sumGroupPrices(groupsData)
  const discountedTotalPrice = groupsData.reduce((acc, group) => acc + group.discountedPrice, 0)
  const vatPriceBreakdown = vatPriceBreakdownCalulator(vatBreakdownConfig, discountedTotalPrice)

  return {
    totalListPrice,
    discountedTotalPrice,
    vatPriceBreakdown,
    finalTotalPrice: calculatePriceIncludingVat(vatBreakdownConfig, discountedTotalPrice)
  }
}

// Calculates effective total discount based on each group's contribution to total price.
export const calculateTotalDiscountFromGroups = (
  discounts: Record<string, number>,
  groupsData: PricingSummaryGroupProps[]
): string => {
  if (groupsData.length === 0) {
    return Constants.DISCOUNT_DEFAULT_VALUE
  }

  const totalPrice = sumGroupPrices(groupsData)
  if (totalPrice <= 0) {
    return Constants.DISCOUNT_DEFAULT_VALUE
  }

  const totalDiscountAmount = groupsData.reduce((sum, group) => {
    const groupDiscount = discounts[group.id] || 0
    return sum + group.price * (groupDiscount / 100)
  }, 0)

  const effectiveDiscount = (totalDiscountAmount / totalPrice) * 100
  return formatDiscountValue(effectiveDiscount)
}

export const isValidDiscountInput = (value: string): boolean => {
  if (value === '') {
    return true
  }

  if (!Constants.DISCOUNT_INPUT_REGEX.test(value)) {
    return false
  }

  if (value !== '.' && value !== '-' && value !== '-.') {
    const numValue = parseFloat(value)
    if (isNaN(numValue) || numValue >= 100 || numValue <= -1000) {
      return false
    }
  }

  return true
}

export const normalizePriceValue = (inputValue: string, fallback: number): string => {
  const parsed = parseNormalizedPrice(inputValue)
  if (Number.isNaN(parsed)) {
    return fallback.toFixed(2)
  }
  return parsed.toFixed(2)
}

export const isValidAmountInput = (value: string): boolean => {
  if (value === '') {
    return true
  }

  return Constants.AMOUNT_INPUT_REGEX.test(value)
}

export const formatDiscountValue = (value: number | string): string => {
  const parsedValue = typeof value === 'string' ? parseFloat(value) : value
  return Number.isNaN(parsedValue) ? '0' : String(value)
}

export const formatDisplayValue = (val: string | number): string => {
  const num = typeof val === 'string' ? parseFloat(val) : val
  if (isNaN(num)) {
    return ''
  }
  return num.toFixed(2)
}

export const getNumericDiscounts = (discounts: Record<string, string>): Record<string, number> => {
  const numericDiscounts: Record<string, number> = {}
  Object.keys(discounts).forEach(key => {
    numericDiscounts[key] = parseFloat(discounts[key]) || 0
  })
  return numericDiscounts
}

export const parseNormalizedPrice = (value: string): number => parseFloat(value.replace(',', '.'))

export const calculateDiscountFromPrice = (baselinePrice: number, targetPrice: number): number => {
  if (baselinePrice <= 0) {
    return 0
  }
  return ((baselinePrice - targetPrice) / baselinePrice) * 100
}

export const hasNonZeroDiscount = (
  isApplyDiscount: boolean,
  discountValue: number | string | null | undefined
): boolean => {
  if (!isApplyDiscount) {
    return false
  }

  const numericDiscount = typeof discountValue === 'string' ? parseFloat(discountValue) : (discountValue ?? 0)
  return Number.isFinite(numericDiscount) && numericDiscount !== 0
}

export const toYearlyPrice = (price: number, isMonthly: boolean): number =>
  isMonthly ? parseFloat((price * 12).toFixed(2)) : price

export const applyDiscountToGroupPricesWithRounding = (groupPrices: Price[], discount: number): Price[] => {
  const normalizedDiscount = parseFloat(formatDiscountValue(discount))

  const rows = groupPrices.map((price, rowIndex) => {
    const rawDiscountedPrice = price.equipmentPrice * (1 - normalizedDiscount / 100)
    return {
      rowIndex,
      price,
      rawDiscountedPrice,
      cents: Math.round(rawDiscountedPrice * 100)
    }
  })

  const roundedGroupTotalCents = rows.reduce((sum, row) => sum + row.cents, 0)
  const targetGroupTotalCents = Math.round(
    groupPrices.reduce((sum, price) => sum + price.equipmentPrice, 0) * (1 - normalizedDiscount / 100) * 100
  )

  const remainderCents = targetGroupTotalCents - roundedGroupTotalCents
  if (remainderCents === 0 || rows.length === 0) {
    return rows.map(row => ({
      ...row.price,
      discountedPrice: Math.max(0, row.cents / 100),
      discount: normalizedDiscount
    }))
  }

  const adjustmentOrder = [...rows].sort((a, b) => {
    const aFraction = a.rawDiscountedPrice * 100 - Math.floor(a.rawDiscountedPrice * 100)
    const bFraction = b.rawDiscountedPrice * 100 - Math.floor(b.rawDiscountedPrice * 100)
    return remainderCents > 0 ? bFraction - aFraction : aFraction - bFraction
  })

  const adjustmentDirection = remainderCents > 0 ? 1 : -1
  const adjustmentCount = Math.abs(remainderCents)
  const orderLength = adjustmentOrder.length
  const fullCycles = Math.floor(adjustmentCount / orderLength)
  const extraAdjustments = adjustmentCount % orderLength
  const orderPositionByRowIndex = new Map(adjustmentOrder.map((row, orderIndex) => [row.rowIndex, orderIndex]))

  return rows.map(row => ({
    ...row.price,
    discountedPrice: Math.max(
      0,
      (row.cents +
        adjustmentDirection *
          (fullCycles +
            ((orderPositionByRowIndex.get(row.rowIndex) ?? Number.MAX_SAFE_INTEGER) < extraAdjustments ? 1 : 0))) /
        100
    ),
    discount: normalizedDiscount
  }))
}

export const redistributeGroupPricesToTargetTotal = (
  groupPrices: Price[],
  targetGroupDiscountedPrice: number
): Price[] => {
  if (groupPrices.length === 0) {
    return groupPrices
  }

  const currentGroupTotalCents = Math.round(
    groupPrices.reduce((sum, currentPrice) => sum + currentPrice.discountedPrice, 0) * 100
  )
  const targetGroupTotalCents = Math.round(targetGroupDiscountedPrice * 100)
  const deltaCents = currentGroupTotalCents - targetGroupTotalCents

  const perEquipmentDelta = Math.trunc(deltaCents / groupPrices.length)
  let remainder = deltaCents % groupPrices.length

  const distributedDeltasByEquipmentId = new Map<string, number>()
  groupPrices.forEach(price => {
    const oneCentAdjustment = remainder === 0 ? 0 : remainder > 0 ? 1 : -1
    distributedDeltasByEquipmentId.set(price.equipmentId, perEquipmentDelta + oneCentAdjustment)
    remainder -= oneCentAdjustment
  })

  return groupPrices.map(price => {
    const distributedDelta = distributedDeltasByEquipmentId.get(price.equipmentId) || 0
    const originalDiscountedCents = Math.round(price.discountedPrice * 100)
    const updatedDiscountedCents = Math.max(0, originalDiscountedCents - distributedDelta)
    const updatedDiscountedPrice = updatedDiscountedCents / 100
    const nextDiscount =
      price.equipmentPrice > 0 ? ((price.equipmentPrice - updatedDiscountedPrice) / price.equipmentPrice) * 100 : 0

    return {
      ...price,
      discountedPrice: updatedDiscountedPrice,
      discount: parseFloat(formatDiscountValue(nextDiscount))
    }
  })
}

export const createUniformGroupDiscounts = (
  groupsData: PricingSummaryGroupProps[],
  value: number
): Record<EquipmentGroupStId, number> =>
  groupsData.reduce((acc, group) => ({ ...acc, [group.id]: value }), {} as Record<EquipmentGroupStId, number>)

export const applyUniformDiscountToGroups = (
  groupsData: PricingSummaryGroupProps[],
  discountValue: number
): PricingSummaryGroupProps[] =>
  groupsData.map(group => ({
    ...group,
    discount: discountValue,
    discountedPrice: calculateDiscountedPrice(group.price, discountValue)
  }))

export const applyDiscountToSingleGroup = (
  groupsData: PricingSummaryGroupProps[],
  groupId: EquipmentGroupStId,
  discountValue: number,
  discountedPrice?: number
): PricingSummaryGroupProps[] =>
  groupsData.map(group => {
    if (group.id === groupId) {
      return {
        ...group,
        discount: discountValue,
        discountedPrice: discountedPrice ?? calculateDiscountedPrice(group.price, discountValue)
      }
    }
    return group
  })

// Filters 'sfSalesOrganizationLimits' based on user profile 'branchOffice' and 'region'.
// This is related to China multilayer discount approval, where different regions and
// branches use different settings. Function does not touch 'sfUserLimits' or
// 'mbsFrontlineDiscountLimit' fields.
export function filterSalesforceDiscountLimitsWithRegionAndBranch(
  profile: ClientProfile,
  discountLimits: DiscountLimits
) {
  const sfOrgLimits = discountLimits.sfSalesOrganizationLimits
  const noSfOrganizationLimits = !sfOrgLimits
  const noMultilevelMatrixRows = sfOrgLimits && !sfOrgLimits.some(dl => dl.approvalLevels != null)
  const noProfileRegion = !profile.region
  if (noSfOrganizationLimits || noMultilevelMatrixRows || noProfileRegion) {
    // We need three things to be present before we start to filter organisation limits:
    // 1. there has to be some organization limits returned from salesforce
    // 2. at least one of the salesforce discount limit rows has to have approvalLevels set.
    // Currently only china has these multilevel rows; so effectively, this means "if china"
    // 3. user's profile has to have region set. In china all users *should* have this in place.
    return discountLimits
  }

  const regionMatch = sfOrgLimits.filter(dl => dl.region === profile.region && !dl.branchOffice)
  if (!profile.branchOffice) {
    // Profile has region, but not branch office; In this case we return region matches where branch is empty
    return {
      ...discountLimits,
      sfSalesOrganizationLimits: regionMatch
    }
  }

  // We are left with case where user profile has Region and Branch. In this case we have to try 2 options:
  // A. Try to match with Region and Branch. If hits are found return them. If not, try option B:
  // B. Return matched with Region
  const branchMatch = sfOrgLimits.filter(dl => dl.region === profile.region && dl.branchOffice === profile.branchOffice)
  if (branchMatch.length > 0) {
    // Option A success. Return matching branches
    return {
      ...discountLimits,
      sfSalesOrganizationLimits: branchMatch
    }
  } else {
    // Use Option B
    return {
      ...discountLimits,
      sfSalesOrganizationLimits: regionMatch
    }
  }
}

export const findMinimumLevelsOfDiscountApproval = (
  discountApprovalLevels: DiscountApprovalLevels[],
  coreDiscountPercentageRounded: number
) => {
  const minimumMatch = discountApprovalLevels.find(
    discountApprovalLevel =>
      coreDiscountPercentageRounded >= discountApprovalLevel.from &&
      coreDiscountPercentageRounded <= discountApprovalLevel.to
  )
  return minimumMatch ? minimumMatch.levels : 0
}

export const validateDiscountPercentage = (discountPercentage: number) =>
  discountPercentage >= -999.99 && discountPercentage <= 99.99

export const getVatDetails = (vatPriceBreakdown: VatBreakdown[]): VatBreakdown[] =>
  vatPriceBreakdown.filter(vatPrice => vatPrice.annualVatPrice > 0)

const discountLimitSourcesThatRequireMessage: DiscountIsLimitedBy[] = [
  'salesforce org',
  'salesforce user',
  'generic salesforce org',
  'generic salesforce user'
]

export function groupContainsLimitSource(group: GroupPriceBreakdown): boolean {
  if (!(group.discountPercentage >= group.maximumSalesPricePercentageWithoutApproval)) {
    return false
  }
  const sectionContainsIt = (prices: { discountIsLimitedBy: DiscountIsLimitedBy }[]): boolean =>
    prices.some(price => discountLimitSourcesThatRequireMessage.includes(price.discountIsLimitedBy))
  return sectionContainsIt(group.elevators.equipmentPrices) || sectionContainsIt(group.escalators.equipmentPrices)
}

export function shouldRequireApprovalMessage(groups: GroupPriceBreakdown[]): boolean {
  return groups.some(groupContainsLimitSource)
}
